"use client";

// Real, Gmail-query-backed computations mirroring the Flutter app's Riverpod
// providers — no fake/sample data, no AI unless the app's own provider used
// AI (subscriptions frequency, spending, daily brief, cleanup rule generator).

import { aiGateway } from "./aiGateway";
import { countMatching, fetchInboxProfile, listSample } from "./gmail";
import { dismissedSuggestionsStore } from "./stores";
import { parseFrom } from "./format";
import type {
  DashboardStats,
  ExploreSuggestion,
  NormalizedEmail,
  RealCleanupSuggestion,
  SenderGroup,
  SenderSummary,
  SubscriptionSummary,
} from "./types";
import { cleanupHistoryStore } from "./stores";

const FALLBACK_AVG_MESSAGE_BYTES = 75000;

export type ScanProgress = { stage: string; current: number; total: number };

async function estimateAvgMessageBytes(accessToken: string, accountEmail: string): Promise<number> {
  const sample = await listSample(accessToken, accountEmail, "", 10);
  const sized = sample.filter((m) => m.size_bytes > 0);
  if (sized.length === 0) return FALLBACK_AVG_MESSAGE_BYTES;
  return Math.floor(sized.reduce((a, m) => a + m.size_bytes, 0) / sized.length);
}

function computeHealthScore(totalEmails: number, unreadEmails: number, promotionsCount: number, oldEmailsCount: number): number {
  if (totalEmails === 0) return 100;
  const unreadRatio = unreadEmails / totalEmails;
  const clutterRatio = (promotionsCount + oldEmailsCount) / totalEmails;
  const score = 100 - unreadRatio * 40 - clutterRatio * 60;
  return Math.round(Math.min(100, Math.max(0, score)));
}

export async function computeDashboardStats(accessToken: string, accountEmail: string): Promise<DashboardStats> {
  const [profile, unreadEmails, promotionsCount, oldEmailsCount, avgBytes] = await Promise.all([
    fetchInboxProfile(accessToken),
    countMatching(accessToken, "is:unread"),
    countMatching(accessToken, "category:promotions"),
    countMatching(accessToken, "older_than:180d"),
    estimateAvgMessageBytes(accessToken, accountEmail),
  ]);

  const totalEmails = profile.messagesTotal;
  const estimatedStorageUsedBytes = avgBytes * totalEmails;
  const estimatedRecoverableBytes = avgBytes * (promotionsCount + oldEmailsCount);

  return {
    inboxHealthScore: computeHealthScore(totalEmails, unreadEmails, promotionsCount, oldEmailsCount),
    totalEmails,
    unreadEmails,
    promotionsCount,
    oldEmailsCount,
    estimatedStorageUsedBytes,
    estimatedRecoverableBytes,
    recentCleanups: cleanupHistoryStore.load().slice(0, 5),
  };
}

const NOTIFICATION_LIKE_PATTERNS = ["noreply", "no-reply", "notification", "alert", "update", "digest"];
function looksLikeNotificationSender(email: string): boolean {
  const lower = email.toLowerCase();
  return NOTIFICATION_LIKE_PATTERNS.some((p) => lower.includes(p));
}

const CATEGORY_DEFS: [string, string, "archive" | "trash"][] = [
  ["Promotions", "category:promotions", "trash"],
  ["Social", "category:social", "archive"],
  ["Updates & Forums", "category:updates OR category:forums", "trash"],
];

/** Real, mailbox-wide cleanup-candidate suggestions: Gmail's own category
 *  counts, plus the top few individual senders from a small recent sample. */
export async function computeExploreSuggestions(
  accessToken: string,
  accountEmail: string,
  onProgress?: (p: ScanProgress) => void,
): Promise<ExploreSuggestion[]> {
  const dismissed = dismissedSuggestionsStore.load();
  const suggestions: ExploreSuggestion[] = [];
  const totalStages = CATEGORY_DEFS.length + 1;

  for (let i = 0; i < CATEGORY_DEFS.length; i += 1) {
    const [label, query, action] = CATEGORY_DEFS[i];
    onProgress?.({ stage: `Checking ${label}`, current: i, total: totalStages });
    const key = `__category_${label}__`;
    if (dismissed.has(key)) continue;
    const count = await countMatching(accessToken, query);
    if (count < 20) continue;
    suggestions.push({ key, title: label, subtitle: "From everyone", count, action, query, isCategory: true });
  }

  onProgress?.({ stage: "Scanning recent senders", current: CATEGORY_DEFS.length, total: totalStages });
  const sample = await listSample(accessToken, accountEmail, "", 30);
  const bySender = new Map<string, string[]>();
  for (const m of sample) {
    const { email, name } = parseFrom(m.from);
    if (!email) continue;
    const names = bySender.get(email) ?? [];
    names.push(name);
    bySender.set(email, names);
  }
  const topSenders = [...bySender.entries()].sort((a, b) => b[1].length - a[1].length).slice(0, 8);

  for (const [email, names] of topSenders) {
    if (dismissed.has(email)) continue;
    const count = await countMatching(accessToken, `from:${email}`);
    if (count < 20) continue;
    const title = names.find((n) => n) || email;
    suggestions.push({
      key: email,
      title,
      subtitle: email,
      count,
      action: looksLikeNotificationSender(email) ? "trash" : "archive",
      query: `from:${email}`,
      isCategory: false,
    });
  }

  return suggestions.sort((a, b) => b.count - a.count);
}

/** Breaks a category suggestion down into the real senders that make it up. */
export async function computeCategoryGroups(
  accessToken: string,
  accountEmail: string,
  categoryQuery: string,
): Promise<SenderGroup[]> {
  const sample = await listSample(accessToken, accountEmail, categoryQuery, 40);
  const seen = new Set<string>();
  const reps: NormalizedEmail[] = [];
  for (const m of sample) {
    const { email } = parseFrom(m.from);
    if (!email || seen.has(email)) continue;
    seen.add(email);
    reps.push(m);
  }
  const top = reps.slice(0, 15);
  const groups: SenderGroup[] = [];
  for (let i = 0; i < top.length; i += 1) {
    const m = top[i];
    const { email, name } = parseFrom(m.from);
    const count = await countMatching(accessToken, `${categoryQuery} from:${email}`);
    if (count < 1) continue;
    groups.push({
      senderEmail: email,
      senderName: name || email,
      category: "",
      totalCount: count,
      lastMessageAt: m.received_at,
      isRead: m.is_read,
      accentIndex: i,
    });
  }
  return groups;
}

function categorizeGroup(m: NormalizedEmail): string {
  if (m.labels.includes("CATEGORY_PROMOTIONS") || m.labels.includes("CATEGORY_UPDATES") || m.labels.includes("CATEGORY_FORUMS")) {
    return "Mailing list";
  }
  if (m.labels.includes("CATEGORY_PERSONAL")) return "Personal";
  const lower = m.from.toLowerCase();
  if (lower.includes("noreply") || lower.includes("no-reply") || lower.includes("newsletter")) return "Mailing list";
  return "";
}

/** The most recent message per unique sender, each annotated with that
 *  sender's real total count in the mailbox. */
export async function computeInboxOverview(
  accessToken: string,
  accountEmail: string,
  onProgress?: (p: ScanProgress) => void,
): Promise<SenderGroup[]> {
  onProgress?.({ stage: "Fetching recent mail", current: 0, total: 1 });
  const sample = await listSample(accessToken, accountEmail, "", 40);

  const seen = new Set<string>();
  const reps: NormalizedEmail[] = [];
  for (const m of sample) {
    const { email } = parseFrom(m.from);
    if (!email || seen.has(email)) continue;
    seen.add(email);
    reps.push(m);
  }
  const top = reps.slice(0, 15);
  const groups: SenderGroup[] = [];
  for (let i = 0; i < top.length; i += 1) {
    const m = top[i];
    const { email, name } = parseFrom(m.from);
    onProgress?.({ stage: `Counting mail from ${name || email}`, current: i + 1, total: top.length });
    const count = await countMatching(accessToken, `from:${email}`);
    if (count < 1) continue;
    groups.push({
      senderEmail: email,
      senderName: name || email,
      category: categorizeGroup(m),
      totalCount: count,
      lastMessageAt: m.received_at,
      isRead: m.is_read,
      accentIndex: i,
    });
  }
  return groups;
}

const CLEANUP_CATEGORY_DEFS: [string, string, string][] = [
  ["Old Promotions", "category:promotions older_than:30d", "Promotional emails older than 30 days rarely need to be kept."],
  ["Old Emails", "older_than:180d -is:starred", "Emails older than 6 months that you haven't starred are safe to clear out."],
  ["Read Newsletters", "unsubscribe is:read", "Newsletters you've already read and could unsubscribe from."],
  ["Large Attachments", "has:attachment larger:5M", "Emails with attachments over 5MB — often the biggest storage users."],
];

/** Real, mailbox-wide cleanup suggestions for the Smart Clean tab — one
 *  cheap accurate count per category, no AI round-trip. */
export async function computeCleanupSuggestions(
  accessToken: string,
  onProgress?: (p: ScanProgress) => void,
): Promise<RealCleanupSuggestion[]> {
  const suggestions: RealCleanupSuggestion[] = [];
  for (let i = 0; i < CLEANUP_CATEGORY_DEFS.length; i += 1) {
    const [label, query, reason] = CLEANUP_CATEGORY_DEFS[i];
    onProgress?.({ stage: `Checking ${label}`, current: i, total: CLEANUP_CATEGORY_DEFS.length });
    const count = await countMatching(accessToken, query);
    if (count < 5) continue;
    suggestions.push({ category: label, reason, estimatedCount: count, estimatedBytes: count * FALLBACK_AVG_MESSAGE_BYTES, query });
  }
  return suggestions.sort((a, b) => b.estimatedCount - a.estimatedCount);
}

/** Sample-based sender aggregation (Sender Manager tab). */
export async function computeSenderSummaries(accessToken: string, accountEmail: string): Promise<SenderSummary[]> {
  const sample = await listSample(accessToken, accountEmail, "", 50);
  const bySender = new Map<string, NormalizedEmail[]>();
  for (const m of sample) {
    const { email } = parseFrom(m.from);
    if (!email) continue;
    const list = bySender.get(email) ?? [];
    list.push(m);
    bySender.set(email, list);
  }
  return [...bySender.entries()]
    .map(([email, messages]) => ({
      email,
      name: parseFrom(messages[0].from).name || email,
      count: messages.length,
      estimatedBytes: messages.reduce((a, m) => a + m.size_bytes, 0),
    }))
    .sort((a, b) => b.count - a.count);
}

const UNSUBSCRIBE_LINK_REGEX = /href="([^"]*unsubscribe[^"]*)"/i;

/** Finds newsletter/marketing senders by searching "unsubscribe" across the
 *  mailbox, with real per-sender counts and AI-estimated send frequency. */
export async function computeSubscriptionSummaries(
  accessToken: string,
  accountEmail: string,
  onProgress?: (p: ScanProgress) => void,
): Promise<SubscriptionSummary[]> {
  onProgress?.({ stage: "Scanning for mailing lists", current: 0, total: 1 });
  const matches = await listSample(accessToken, accountEmail, "unsubscribe", 200);

  const bySender = new Map<string, NormalizedEmail[]>();
  for (const m of matches) {
    const { email } = parseFrom(m.from);
    if (!email) continue;
    const list = bySender.get(email) ?? [];
    list.push(m);
    bySender.set(email, list);
  }
  const senders = [...bySender.entries()];

  const realCounts = new Map<string, number>();
  for (let i = 0; i < senders.length; i += 1) {
    const [email, messages] = senders[i];
    onProgress?.({ stage: `Counting ${parseFrom(messages[0].from).name}`, current: i + 1, total: senders.length });
    realCounts.set(email, await countMatching(accessToken, `unsubscribe from:${email}`));
  }

  const payload = senders.map(([email, messages]) => ({
    sender: email,
    name: parseFrom(messages[0].from).name,
    count: realCounts.get(email) ?? messages.length,
    dates: messages.map((m) => m.received_at),
  }));

  let frequencies: Record<string, string> = {};
  try {
    const { subscriptions } = await aiGateway.analyzeSubscriptions(payload);
    frequencies = Object.fromEntries(subscriptions.map((s) => [s.sender, s.frequency]));
  } catch {
    // fall back to "Unknown" if the gateway is unreachable
  }

  return senders
    .map(([email, messages]) => ({
      senderEmail: email,
      senderName: parseFrom(messages[0].from).name,
      emailCount: realCounts.get(email) ?? messages.length,
      frequency: frequencies[email] ?? "Unknown",
      unsubscribeUrl: messages.map((m) => m.snippet.match(UNSUBSCRIBE_LINK_REGEX)?.[1]).find((u) => u) ?? null,
    }))
    .sort((a, b) => b.emailCount - a.emailCount);
}

export async function computeSpendingInsights(accessToken: string, accountEmail: string) {
  const receipts = await listSample(accessToken, accountEmail, 'receipt OR invoice OR "order confirmation"', 40);
  if (receipts.length === 0) return { categories: [], total: 0, insights: [] };
  return aiGateway.spendingInsights(
    receipts.map((m) => ({ from: m.from, subject: m.subject, snippet: m.snippet, date: m.received_at })),
  );
}

/** Locally-derived email habits (no AI call) — busiest day, average volume,
 *  peak hour and unread ratio from a real recent sample. */
export async function computeEmailHabits(accessToken: string, accountEmail: string) {
  const sample = await listSample(accessToken, accountEmail, "", 50);
  if (sample.length === 0) return { busiestDay: "-", avgPerDay: "0", unreadRatio: 0, peakHour: null as number | null };

  const byDay = new Map<number, number>();
  const byHour = new Map<number, number>();
  let unread = 0;
  let earliest = Date.now();
  for (const m of sample) {
    const d = new Date(m.received_at);
    byDay.set(d.getDay(), (byDay.get(d.getDay()) ?? 0) + 1);
    byHour.set(d.getHours(), (byHour.get(d.getHours()) ?? 0) + 1);
    if (!m.is_read) unread += 1;
    earliest = Math.min(earliest, d.getTime());
  }
  const busiestDayIndex = [...byDay.entries()].sort((a, b) => b[1] - a[1])[0][0];
  const peakHour = [...byHour.entries()].sort((a, b) => b[1] - a[1])[0][0];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const spanDays = Math.min(999, Math.max(1, Math.round((Date.now() - earliest) / 86400000)));

  return {
    busiestDay: dayNames[busiestDayIndex],
    avgPerDay: (sample.length / spanDays).toFixed(1),
    unreadRatio: unread / sample.length,
    peakHour,
  };
}

export async function computeAiDailyBrief(accessToken: string, accountEmail: string): Promise<string> {
  const recent = await listSample(accessToken, accountEmail, "", 15);
  const content = recent
    .map((m) => `- From ${m.from}: "${m.subject}" — ${m.snippet}`)
    .join("\n");
  const { summary } = await aiGateway.summarize("daily", content || "Inbox has no recent messages.");
  return summary;
}

export async function computeInboxSummary(
  accessToken: string,
  accountEmail: string,
  scope: "daily" | "weekly",
): Promise<string> {
  const query = scope === "weekly" ? "newer_than:7d" : "newer_than:1d";
  const recent = await listSample(accessToken, accountEmail, query, 30);
  const content = recent.map((m) => `- From ${m.from}: "${m.subject}" — ${m.snippet}`).join("\n");
  const { summary } = await aiGateway.summarize(scope, content || "No messages in this period.");
  return summary;
}

export function whatsGood(stats: DashboardStats): string[] {
  const items: string[] = [];
  if (stats.unreadEmails < stats.totalEmails * 0.1) items.push("Low unread email count");
  if (stats.oldEmailsCount < 50) items.push("Few old emails piling up");
  if (items.length === 0) items.push("Your inbox is under control");
  return items;
}

export function improveBy(stats: DashboardStats): string[] {
  const items: string[] = [];
  if (stats.promotionsCount > 20) items.push(`Reviewing ${stats.promotionsCount} promotional emails`);
  if (stats.oldEmailsCount > 50) items.push(`Clearing ${stats.oldEmailsCount} old emails`);
  if (items.length === 0) items.push("Nothing urgent — nice work");
  return items;
}
